import React from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { setEnrichmentDialogOpen } from '../../store/flowSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from '../../i18n/hooks';

interface ContentQualityActionCardProps {
  tableId: string;
  className?: string;
  maxActions?: number;
  onActionClick?: (actionType: string) => void;
}

const ContentQualityActionCard = ({
  tableId,
  className,
  onActionClick,
}: ContentQualityActionCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {suggestedActions: actions} = useSelector((state: RootState) => state.score);
  const isLoading = actions == null;
 

  if (isLoading) {
    return (
      <Card className={cn(
        "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-none relative overflow-hidden ring-1 ring-black/5 p-6",
        className
      )}>
        <div className="animate-pulse">
          <div className="h-8 bg-white/20 rounded-md mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-white/10 rounded-xl"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (actions.length === 0) {
    return (
      <Card className={cn(
        "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-none relative overflow-hidden ring-1 ring-black/5 p-6",
        className
      )}>
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="h-5 w-5 text-white/80" />
          <h3 className="font-semibold text-xl">{t('dashboard.content_quality')}</h3>
        </div>
        <p className="text-white/80">
          {t('productsView.excellent_content_message')}
        </p>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-none relative overflow-hidden ring-1 ring-black/5 p-6",
      className
    )}>
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="h-5 w-5 text-white/80" />
        <h3 className="font-semibold text-lg md:text-xl">{t('dashboard.recommended_actions')}</h3>
      </div>

      <div className="space-y-3">
        {actions.map((action, index) => (
          <Button
            key={index}
            variant="ghost"
            onClick={() => {
              if (onActionClick) {
                onActionClick(action.action);
              }
              dispatch(setEnrichmentDialogOpen({
                isOpen: true,
                selectedFeatures: action.enrichmentFeatures
              }));
            }}
            className="w-full bg-white hover:bg-white group px-4 py-3 h-auto rounded-xl transition-all overflow-hidden"
          >
            <div className="flex items-start w-full">
              <div className="w-10 h-10 hidden md:flex flex-shrink-0 items-center justify-center rounded-lg 
                           bg-blue-50 group-hover:bg-blue-100/80 transition-colors mt-1 mr-3">
                <action.icon className="h-4 w-4 text-blue-600" />
              </div>
              
              <div className="flex flex-col min-w-0 flex-1 text-left mr-2">
                <span className="font-medium text-gray-900 text-base truncate">
                  {action.action}
                </span>
                <div className="relative w-full">
                  <span className={cn(
                    "text-sm block truncate transition-all duration-200",
                    "group-hover:opacity-0 group-hover:translate-y-2",
                    action.score > 60 ? "text-blue-600" : "text-purple-600"
                  )}>
                    {action.subtext}
                  </span>
                  <span className="text-sm font-medium text-blue-600 absolute top-0 left-0
                               opacity-0 group-hover:opacity-100 transition-all duration-200
                               -translate-y-2 group-hover:translate-y-0 truncate w-full">
                    {t('dashboard.fix_now')}
                  </span>
                </div>
              </div>

              <div className="flex-shrink-0 ml-2 mt-1.5">
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 
                                     transition-all" />
              </div>
            </div>
          </Button>
        ))}
      </div>
    </Card>
  );
};

export default ContentQualityActionCard;